import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import React, { forwardRef, useCallback, useEffect } from 'react';

import styled from '../styled';
import { usePrefersReducedMotion, useRailEdges } from './hooks';
import { PillRow } from './primitives';
import { C } from './tokens';

/** Share of the visible rail one arrow press moves. */
const PAGE_FRACTION = 0.8;

const Wrap = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
`;

/**
 * Only pointer-and-hover devices get the arrows; a finger swipes the rail and
 * the fades already say there is more. `display` stays off elsewhere so the
 * arrows never reach assistive technology on phones either.
 */
const Arrow = styled.button<{ $side: 'left' | 'right' }>`
  display: none;
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 0;
  z-index: 2;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: #fff;
  color: ${C.forest900};
  box-shadow: inset 0 0 0 1px ${C.canvas900}, 0 2px 8px rgba(15, 15, 15, 0.12);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  transition: background 200ms ease, color 200ms ease, box-shadow 200ms ease;
  &:hover {
    background: ${C.forest900};
    color: #fff;
    box-shadow: 0 2px 8px rgba(15, 15, 15, 0.16);
  }
  &:focus-visible {
    outline: 2px solid ${C.evergreen500};
    outline-offset: 2px;
  }
  @media (hover: hover) and (pointer: fine) {
    display: inline-flex;
  }
`;

type TrackComponent = typeof PillRow;

export type ChipRailProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'children'
> & {
  children: React.ReactNode;
  /** The scrolling row; defaults to `PillRow`. Pass a `styled(PillRow)`. */
  track?: TrackComponent;
  /** Values whose change should re-measure the overflow. */
  deps?: unknown[];
  /** Spoken name of what the row holds, e.g. "categories". */
  label?: string;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
};

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
  } else {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

/**
 * A horizontally scrolling chip row that desktop users can actually page
 * through. Keeps `PillRow`'s native swipe, drag, and edge fades, and adds
 * left/right arrows (pointer devices only, hidden at each end) plus a wheel
 * handler that turns a vertical wheel into horizontal travel while the row has
 * room left to scroll in that direction.
 */
const ChipRail = forwardRef<HTMLDivElement, ChipRailProps>(function ChipRail(
  {
    children,
    track: Track = PillRow,
    deps = [],
    label,
    wrapperClassName,
    wrapperStyle,
    style,
    ...trackProps
  },
  forwardedRef,
) {
  const { ref, edges } = useRailEdges<HTMLDivElement>(deps);
  const reduceMotion = usePrefersReducedMotion();

  const setTrack = useCallback(
    (el: HTMLDivElement | null) => {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      assignRef(forwardedRef, el);
    },
    [forwardedRef, ref],
  );

  const page = (dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: dir * Math.max(1, Math.round(el.clientWidth * PAGE_FRACTION)),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (event.deltaX !== 0 || event.deltaY === 0) return;
      if (event.ctrlKey || event.metaKey) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) return;
      const x = el.scrollLeft;
      const canMove = event.deltaY > 0 ? x < max - 1 : x > 1;
      if (!canMove) return;
      event.preventDefault();
      el.scrollLeft = Math.max(0, Math.min(max, x + event.deltaY));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [ref]);

  const what = label ? ` ${label}` : '';

  return (
    <Wrap className={wrapperClassName} style={wrapperStyle}>
      {!edges.fits && !edges.atStart ? (
        <Arrow
          type="button"
          $side="left"
          aria-label={`Scroll${what} left`}
          data-rail-arrow="prev"
          onClick={() => page(-1)}
        >
          <CaretLeft size={16} weight="bold" />
        </Arrow>
      ) : null}
      <Track
        {...trackProps}
        ref={setTrack}
        style={
          {
            ...style,
            '--fl': edges.fl,
            '--fr': edges.fr,
          } as React.CSSProperties
        }
      >
        {children}
      </Track>
      {!edges.fits && !edges.atEnd ? (
        <Arrow
          type="button"
          $side="right"
          aria-label={`Scroll${what} right`}
          data-rail-arrow="next"
          onClick={() => page(1)}
        >
          <CaretRight size={16} weight="bold" />
        </Arrow>
      ) : null}
    </Wrap>
  );
});

export default ChipRail;
